/** Gemeinsame DOM-Helfer und öffentliche Style-Funktionen für TravelMenuDOM. */
import { fontBase, fontXs, padSm, radius } from './UITheme';
import type { OwnPlanet } from './TravelMenuState';

export const HOLO_BG = 'linear-gradient(180deg,rgba(8,20,45,0.95),rgba(4,12,28,0.92))';
export const HOLO_BRD = '1px solid rgba(100,180,255,0.25)';
export const HOLO_GLOW = '-4px 0 20px rgba(80,160,255,0.12),inset 0 1px 0 rgba(100,180,255,0.12)';
export const SCAN = 'repeating-linear-gradient(0deg,transparent,transparent 3px,rgba(100,180,255,0.02) 3px,rgba(100,180,255,0.02) 4px)';
export const CLR_PRIMARY = '#8cf';
export const CLR_TEXT = '#c0d0e0';
export const CLR_DIM = '#5a7a9a';
export const BORDER_SUB = '1px solid rgba(100,180,255,0.12)';

export function div(style: Partial<CSSStyleDeclaration>): HTMLDivElement {
  const el = document.createElement('div');
  Object.assign(el.style, style);
  return el;
}

export function smallBtn(text: string): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.textContent = text;
  Object.assign(btn.style, {
    background: 'rgba(100,180,255,0.08)', color: CLR_PRIMARY,
    border: BORDER_SUB, fontFamily: 'monospace', cursor: 'pointer',
    fontSize: fontXs(), padding: `2px ${padSm()}`, borderRadius: radius(),
  });
  btn.addEventListener('pointerenter', () => { btn.style.filter = 'brightness(1.2)'; });
  btn.addEventListener('pointerleave', () => { btn.style.filter = ''; });
  return btn;
}

export function lasche(text: string, active: boolean): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.textContent = text;
  Object.assign(btn.style, {
    writingMode: 'vertical-rl', textOrientation: 'mixed',
    padding: '10px 5px', minHeight: '44px',
    background: active ? HOLO_BG : 'rgba(4,10,22,0.7)',
    border: active ? HOLO_BRD : '1px solid rgba(100,180,255,0.1)',
    borderRight: 'none', borderRadius: '6px 0 0 6px',
    color: active ? '#bdf' : '#3a5a7a', fontFamily: 'monospace',
    fontSize: '10px', fontWeight: active ? '700' : '400',
    letterSpacing: '2px', cursor: 'pointer',
    transition: 'background 150ms, color 150ms, border-color 150ms',
    boxShadow: active ? '-2px 0 8px rgba(80,160,255,0.08)' : 'none',
  });
  return btn;
}

export function wizardSection(label: string): HTMLDivElement {
  const section = div({ display: 'flex', flexDirection: 'column', gap: '6px' });
  const header = document.createElement('span');
  header.textContent = label;
  Object.assign(header.style, { fontSize: '11px', color: CLR_DIM, fontWeight: '700', letterSpacing: '2px', marginBottom: '2px' });
  section.appendChild(header);
  return section;
}

export function selectEl(placeholder: string): HTMLSelectElement {
  const sel = document.createElement('select');
  Object.assign(sel.style, {
    background: 'rgba(4,10,22,0.7)', color: CLR_TEXT,
    border: BORDER_SUB, borderRadius: radius(),
    padding: padSm(), fontFamily: 'monospace', fontSize: fontBase(),
    width: '100%', cursor: 'pointer',
  });
  const opt = document.createElement('option');
  opt.value = ''; opt.textContent = placeholder;
  sel.appendChild(opt);
  return sel;
}

export function mapBtn(text: string): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.textContent = `\u{1F3AF} ${text}`;
  Object.assign(btn.style, {
    background: 'transparent', color: CLR_DIM,
    border: '1px solid rgba(100,180,255,0.15)',
    fontFamily: 'monospace', cursor: 'pointer',
    fontSize: fontXs(), padding: `4px ${padSm()}`,
    borderRadius: radius(), alignSelf: 'flex-start',
    transition: 'color 150ms, border-color 150ms',
  });
  btn.addEventListener('pointerdown', (e) => e.stopPropagation());
  btn.addEventListener('pointerenter', () => { btn.style.color = CLR_PRIMARY; btn.style.borderColor = 'rgba(100,180,255,0.4)'; });
  btn.addEventListener('pointerleave', () => { btn.style.color = CLR_DIM; btn.style.borderColor = 'rgba(100,180,255,0.15)'; });
  return btn;
}

export function modeCard(name: string, desc: string, icon: string, enabled: boolean): HTMLDivElement {
  const card = div({
    display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px',
    background: 'rgba(100,180,255,0.04)', border: BORDER_SUB, borderRadius: radius(),
    cursor: enabled ? 'pointer' : 'default', opacity: enabled ? '1' : '0.4',
    transition: 'background 150ms, border-color 150ms',
  });
  card.dataset.enabled = enabled ? '1' : '0';
  const iconEl = document.createElement('span');
  iconEl.textContent = icon;
  iconEl.style.fontSize = '20px';
  const textWrap = div({ display: 'flex', flexDirection: 'column', gap: '1px' });
  const nameEl = document.createElement('span');
  nameEl.textContent = name;
  Object.assign(nameEl.style, { color: CLR_PRIMARY, fontSize: fontXs(), fontWeight: '700' });
  const descEl = document.createElement('span');
  descEl.textContent = desc; descEl.className = 'mode-desc';
  Object.assign(descEl.style, { color: CLR_DIM, fontSize: fontXs() });
  textWrap.appendChild(nameEl); textWrap.appendChild(descEl);
  card.appendChild(iconEl); card.appendChild(textWrap);
  if (enabled) {
    card.addEventListener('pointerenter', () => { card.style.background = 'rgba(100,180,255,0.08)'; card.style.borderColor = 'rgba(100,180,255,0.3)'; });
    card.addEventListener('pointerleave', () => { card.style.background = 'rgba(100,180,255,0.04)'; card.style.borderColor = 'rgba(100,180,255,0.12)'; });
  }
  return card;
}

export function populatePlanetSelect(select: HTMLSelectElement, planets: OwnPlanet[], placeholder: string): void {
  select.innerHTML = '';
  const def = document.createElement('option');
  def.value = ''; def.textContent = placeholder;
  select.appendChild(def);
  for (const p of planets) {
    const opt = document.createElement('option');
    opt.value = p.id;
    let total = 0; for (let i = 0; i < p.ships.length; i++) total += p.ships[i];
    opt.textContent = `${p.label}  [${total} Schiffe]`;
    select.appendChild(opt);
  }
}

export function setLascheActive(btn: HTMLButtonElement, active: boolean): void {
  Object.assign(btn.style, {
    background: active ? HOLO_BG : 'rgba(4,10,22,0.7)',
    color: active ? '#bdf' : '#3a5a7a', fontWeight: active ? '700' : '400',
    borderColor: active ? 'rgba(100,180,255,0.25)' : 'rgba(100,180,255,0.1)',
    borderRight: 'none', boxShadow: active ? '-2px 0 8px rgba(80,160,255,0.08)' : 'none',
  });
}

export function setModeCardActive(card: HTMLDivElement, active: boolean): void {
  if (card.dataset.enabled !== '1') return;
  Object.assign(card.style, {
    background: active ? 'rgba(136,204,255,0.15)' : 'rgba(100,180,255,0.04)',
    borderColor: active ? 'rgba(136,204,255,0.4)' : 'rgba(100,180,255,0.12)',
  });
}

export function updateStepIndicator(container: HTMLDivElement, current: number): void {
  const dots = container.querySelectorAll<HTMLSpanElement>('[data-step]');
  dots.forEach(dot => {
    const step = Number(dot.dataset.step);
    dot.style.background = step <= current ? CLR_PRIMARY : 'rgba(100,180,255,0.2)';
    dot.style.width = step === current ? '10px' : '8px';
    dot.style.height = step === current ? '10px' : '8px';
  });
}
